// 计算调度器，用于实现"一变全算"的自动计算逻辑
// 根据数据变化自动触发相关计算函数，确保计算顺序正确
import store from "../store";

// 自定义JSON序列化函数，用于处理循环引用
function safeJsonStringify(obj) {
  const seen = new Set();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        // 处理循环引用，返回一个占位符或undefined
        return undefined;
      }
      seen.add(value);
    }
    return value;
  });
}
import {
  totalTable,
  jingying,
  ranliao,
  gzfl,
  deprecia,
  amortization,
  calculateOperatingRevenue,
  workingCapital,
  Financing,
  // calculateAnnualInvestment,
  totalCost,
  profitCalculation,
  allInvestmentCashFlowCalculation,
  capitalCashFlowCalculation,
  financialPlanCashFlowCalculation,
  calculateRepaymentPlan,
  breakevenAnalysisCalculation,
} from "./comp.js";

// 计算函数依赖关系表
// key: 计算函数名称
// value: 依赖的计算函数名称数组
const calculationDependencies = {
  // 总投资估算：初始计算时不依赖Financing，后续会用Financing计算的利息重新计算
  totalTable: [],

  // 经营费用和燃料费用是独立计算
  jingying: [],
  ranliao: [],
  gzfl: [],

  // 折旧费用依赖于总投资估算
  deprecia: ["totalTable"],

  // 摊销费用依赖于总投资估算
  amortization: ["totalTable"],

  // 营业收入计算依赖于基础数据
  calculateOperatingRevenue: [],

  // 流动资金估算依赖于营业收入和成本费用
  workingCapital: ["calculateOperatingRevenue"],

  // 资金筹措依赖于总投资估算
  Financing: ["totalTable"],

  // 分年投资计划依赖于总投资估算和资金筹措
  // calculateAnnualInvestment: ['totalTable', 'Financing'],

  // 总成本费用依赖于经营费用、燃料费用、工资福利、折旧、摊销等
  totalCost: [
    "jingying",
    "ranliao",
    "gzfl",
    "deprecia",
    "amortization",
    "Financing",
  ],

  // 利润计算依赖于营业收入和总成本费用
  profitCalculation: ["calculateOperatingRevenue", "totalCost"],

  // 现金流量计算依赖于利润计算
  allInvestmentCashFlowCalculation: ["profitCalculation"],
  capitalCashFlowCalculation: ["profitCalculation"],
  financialPlanCashFlowCalculation: ["profitCalculation"],

  // 还款计划计算依赖于资金筹措和利润计算
  calculateRepaymentPlan: ["Financing", "profitCalculation"],

  // 盈亏平衡分析依赖于利润计算
  breakevenAnalysisCalculation: ["profitCalculation"],
};

// 计算函数映射表
const calculationFunctions = {
  totalTable,
  jingying,
  ranliao,
  gzfl,
  deprecia,
  amortization,
  calculateOperatingRevenue,
  workingCapital,
  Financing,
  // calculateAnnualInvestment,
  totalCost,
  profitCalculation,
  allInvestmentCashFlowCalculation,
  capitalCashFlowCalculation,
  financialPlanCashFlowCalculation,
  calculateRepaymentPlan,
  breakevenAnalysisCalculation,
};

// 计算执行顺序（根据依赖关系排序）
const executionOrder = [
  "totalTable",
  "jingying",
  "ranliao",
  "gzfl",
  "deprecia",
  "amortization",
  "calculateOperatingRevenue",
  "workingCapital",
  "Financing",
  // 'calculateAnnualInvestment',//
  "totalCost",
  "profitCalculation",
  "allInvestmentCashFlowCalculation",
  "capitalCashFlowCalculation",
  "financialPlanCashFlowCalculation",
  "calculateRepaymentPlan",
  "breakevenAnalysisCalculation",
];

// 计算执行状态
let isCalculating = false;
let calculationQueue = [];
let lastCalculationTime = 0;
const MIN_CALCULATION_INTERVAL = 100; // 最小计算间隔，避免频繁触发

// 计算调度器类
class CalculationScheduler {
  constructor() {
    this.calculatedFunctions = new Set();
    this.pendingFunctions = new Set();
    this.lastCalculationResult = null;
  }

  // 重置计算状态
  reset() {
    this.calculatedFunctions.clear();
    this.pendingFunctions.clear();
  }

  // 计算函数依赖的所有函数
  getAllDependencies(funcName) {
    const dependencies = new Set();

    const findDependencies = (name) => {
      const deps = calculationDependencies[name] || [];
      deps.forEach((dep) => {
        dependencies.add(dep);
        findDependencies(dep);
      });
    };

    findDependencies(funcName);
    return dependencies;
  }

  // 执行单个计算函数
  async executeFunction(funcName) {
    if (this.calculatedFunctions.has(funcName)) {
      return; // 已经计算过，跳过
    }

    // 检查依赖是否都已计算
    const dependencies = calculationDependencies[funcName] || [];
    const uncalculatedDeps = dependencies.filter(
      (dep) => !this.calculatedFunctions.has(dep)
    );

    if (uncalculatedDeps.length > 0) {
      // 先计算依赖
      for (const dep of uncalculatedDeps) {
        await this.executeFunction(dep);
      }
    }

    // 执行当前函数
    // console.log(`Executing calculation: ${funcName}`);
    const func = calculationFunctions[funcName];
    if (typeof func === "function") {
      try {
        await func();
        this.calculatedFunctions.add(funcName);
      } catch (error) {
        console.error(`Error executing ${funcName}:`, error);
      }
    } else {
      console.error(`Calculation function ${funcName} not found`);
    }
  }

  // 根据数据变化自动触发相关计算
  async triggerCalculations(changedPaths = []) {
    // 检查是否正在计算，避免递归调用
    if (isCalculating) {
      // 检查是否在短时间内已经触发过计算，避免频繁入队
      const now = Date.now();
      if (now - lastCalculationTime > MIN_CALCULATION_INTERVAL) {
        calculationQueue.push(changedPaths);
      }
      return;
    }

    // 检查时间间隔，避免过于频繁的计算
    const now = Date.now();
    if (now - lastCalculationTime < MIN_CALCULATION_INTERVAL) {
      setTimeout(() => {
        this.triggerCalculations(changedPaths);
      }, MIN_CALCULATION_INTERVAL - (now - lastCalculationTime));
      return;
    }

    try {
      isCalculating = true;
      lastCalculationTime = now;
      this.reset();

      // 获取当前项目数据，用于比较计算前后是否有变化
      const beforeData = safeJsonStringify(store.state.projectData);

      // 根据数据变化确定需要重新计算的函数
      // 这里简化处理，直接执行所有计算函数
      // 实际可以根据changedPaths优化，只执行相关函数
      for (const funcName of executionOrder) {
        await this.executeFunction(funcName);
      }

      // 获取计算后的数据
      const afterData = safeJsonStringify(store.state.projectData);

      // 只有当数据真正变化时才记录计算完成
      if (beforeData !== afterData) {
        // console.log("All calculations completed successfully");
        this.lastCalculationResult = afterData;
      }
    } finally {
      isCalculating = false;

      // 处理队列中的计算请求，但最多处理一个，避免无限循环
      if (calculationQueue.length > 0) {
        const nextPaths = calculationQueue.shift();
        // 清空队列，只处理最近的一个请求
        calculationQueue = [];
        this.triggerCalculations(nextPaths);
      }
    }
  }

  // 执行完整的计算流程
  async executeFullCalculation() {
    await this.triggerCalculations();
  }
}

// 创建单例实例
const calculationScheduler = new CalculationScheduler();

export default calculationScheduler;

// 导出便捷的计算触发函数
export function triggerAutoCalculation(changedPaths = []) {
  calculationScheduler.triggerCalculations(changedPaths);
}

export function executeFullCalculation() {
  calculationScheduler.executeFullCalculation();
}

